import { QRCodeSVG } from "qrcode.react";
import { Download, Phone, QrCode } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings, mediaUrl } from "@/context/SettingsContext";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";

const esc = (v = "") => String(v).replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");

export const buildVCard = (m, st, compact = false) => {
  const parts = m.name.trim().split(/\s+/);
  const last = parts.length > 1 ? parts[parts.length - 1] : "";
  const first = parts.length > 1 ? parts.slice(0, -1).join(" ") : parts[0];
  const site = st.website ? (st.website.startsWith("http") ? st.website : `https://${st.website}`) : "";
  return [
    "BEGIN:VCARD", "VERSION:3.0",
    `N:${esc(last)};${esc(first)};;;`, `FN:${esc(m.name)}`,
    `ORG:${esc(st.company_name)}`, `TITLE:${esc(m.role)}`,
    m.phone ? `TEL;TYPE=CELL,VOICE:${m.phone.replace(/\s+/g, "")}` : "",
    st.email ? `EMAIL;TYPE=WORK:${st.email}` : "",
    site ? `URL:${site}` : "",
    !compact && st.address ? `ADR;TYPE=WORK:;;${esc(st.address)};;;;` : "",
    !compact && st.slogan ? `NOTE:${esc(st.slogan)}` : "",
    "END:VCARD",
  ].filter(Boolean).join("\r\n");
};

const slug = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase();

const Card = ({ m, i, st }) => {
  const { t } = useLanguage();
  const vcf = buildVCard(m, st);
  const download = () => {
    const url = URL.createObjectURL(new Blob([vcf], { type: "text/vcard;charset=utf-8" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `${slug(m.name)}-${slug(st.company_name)}.vcf` });
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const initials = m.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <Reveal delay={0.1 * i}>
      <div className="flex h-full flex-col overflow-hidden rounded-3xl border border-brand/10 bg-white shadow-xl shadow-brand/5" data-testid={`vcard-${i}`}>
        <div className="flex items-center gap-4 border-b border-brand/10 bg-mint/60 p-6">
          {m.photo_url ? <img src={mediaUrl(m.photo_url)} alt={m.name} className="h-14 w-14 rounded-2xl object-cover" /> : <span className={`font-display flex h-14 w-14 items-center justify-center rounded-2xl text-lg font-bold text-white ${i % 2 === 0 ? "bg-gold" : "bg-brand"}`}>{initials}</span>}
          <div className="min-w-0">
            <p className="font-display truncate text-lg font-bold text-forest" data-testid={`vcard-${i}-name`}>{m.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">{m.role}</p>
          </div>
        </div>
        <div className="flex flex-1 flex-col items-center gap-5 p-6 sm:flex-row sm:items-start">
          <div className="rounded-2xl border border-brand/10 bg-white p-3" data-testid={`vcard-${i}-qr`}>
            <QRCodeSVG value={buildVCard(m, st, true)} size={124} level="M" fgColor="#0F4D2A" bgColor="#FFFFFF" />
          </div>
          <div className="flex w-full flex-1 flex-col gap-2.5 text-center sm:text-left">
            <p className="inline-flex items-center justify-center gap-1.5 text-xs text-brand-hover sm:justify-start"><QrCode className="h-3.5 w-3.5" />{t.vcard.scan}</p>
            <button type="button" onClick={download} data-testid={`vcard-${i}-download`} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-gold px-4 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-white shadow-lg shadow-gold/25 transition-all hover:-translate-y-0.5 hover:bg-gold-hover"><Download className="h-4 w-4" />{t.vcard.download}</button>
            {m.phone && <a href={`tel:${m.phone.replace(/\s+/g, "")}`} data-testid={`vcard-${i}-call`} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-brand/20 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.12em] text-brand transition-colors hover:bg-mint"><Phone className="h-4 w-4" />{m.phone}</a>}
          </div>
        </div>
      </div>
    </Reveal>
  );
};

const BusinessCards = () => {
  const { t } = useLanguage();
  const { settings: st } = useSettings();
  const members = st.content?.team?.length ? st.content.team : t.leadership.members;
  return (
    <section className="bg-cream/60 py-24" data-testid="vcard-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead overline={t.vcard.overline} title={t.vcard.title} accent={t.vcard.accent} sub={t.vcard.sub} />
        <div className={`mt-14 grid gap-6 md:grid-cols-2 ${members.length > 2 ? "xl:grid-cols-3" : ""}`}>
          {members.map((m, i) => <Card key={i} m={m} i={i} st={st} />)}
        </div>
      </div>
    </section>
  );
};

export default BusinessCards;
